import { useState, useCallback } from "react"
import { lumi } from "../lib/lumi"

// ==================== 타입 정의 ====================

export type ScheduleType =
  | "vacation_full"
  | "vacation_am"
  | "vacation_pm"
  | "business_trip"
  | "field_work"
  | "training"
  | "task"
  | "etc"

export interface ScheduleAttendee {
  userId: string
  userName: string
  teamName?: string
  email?: string
}

export interface TeamSchedule {
  _id: string
  scheduleType: ScheduleType
  title?: string
  description?: string
  startDate: string // YYYY-MM-DD
  endDate: string // YYYY-MM-DD
  attendees: ScheduleAttendee[]
  creator: string
  creatorName?: string
  creatorEmail?: string
  createdAt: string
  updatedAt: string
}

export interface ScheduleInput {
  scheduleType: ScheduleType
  title?: string
  description?: string
  startDate: string
  endDate: string
  attendees: ScheduleAttendee[]
  creatorName?: string
  creatorEmail?: string
}

// ==================== 유형 메타 ====================

export interface ScheduleTypeMeta {
  key: ScheduleType
  label: string
  /** 뱃지 배경/글자/테두리 */
  chip: string
  /** 달력 점 색상 */
  dot: string
  /** 연차·반차 계열 여부 (중복 검사 대상) */
  isVacation: boolean
}

export const SCHEDULE_TYPE_META: Record<ScheduleType, ScheduleTypeMeta> = {
  vacation_full: {
    key: "vacation_full",
    label: "연차",
    chip: "bg-rose-100 text-rose-700 border-rose-200",
    dot: "bg-rose-500",
    isVacation: true,
  },
  vacation_am: {
    key: "vacation_am",
    label: "오전반차",
    chip: "bg-rose-50 text-rose-600 border-rose-200",
    dot: "bg-rose-400",
    isVacation: true,
  },
  vacation_pm: {
    key: "vacation_pm",
    label: "오후반차",
    chip: "bg-rose-50 text-rose-600 border-rose-200",
    dot: "bg-rose-400",
    isVacation: true,
  },
  business_trip: {
    key: "business_trip",
    label: "출장",
    chip: "bg-blue-100 text-blue-700 border-blue-200",
    dot: "bg-blue-500",
    isVacation: false,
  },
  field_work: {
    key: "field_work",
    label: "외근",
    chip: "bg-cyan-100 text-cyan-700 border-cyan-200",
    dot: "bg-cyan-500",
    isVacation: false,
  },
  training: {
    key: "training",
    label: "교육",
    chip: "bg-violet-100 text-violet-700 border-violet-200",
    dot: "bg-violet-500",
    isVacation: false,
  },
  task: {
    key: "task",
    label: "업무",
    chip: "bg-orange-100 text-orange-700 border-orange-200",
    dot: "bg-orange-500",
    isVacation: false,
  },
  etc: {
    key: "etc",
    label: "기타",
    chip: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-500",
    isVacation: false,
  },
}

export const SCHEDULE_TYPE_LIST: ScheduleTypeMeta[] = [
  SCHEDULE_TYPE_META.vacation_full,
  SCHEDULE_TYPE_META.vacation_am,
  SCHEDULE_TYPE_META.vacation_pm,
  SCHEDULE_TYPE_META.business_trip,
  SCHEDULE_TYPE_META.field_work,
  SCHEDULE_TYPE_META.training,
  SCHEDULE_TYPE_META.task,
  SCHEDULE_TYPE_META.etc,
]

// ==================== 날짜 유틸 ====================

/** YYYY-MM-DD 형식 보정 */
export function toDateKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

/** 해당 월의 1일 / 말일 키 */
export function getMonthRange(year: number, month: number): {
  start: string
  end: string
} {
  const start = `${year}-${String(month).padStart(2, "0")}-01`
  const lastDay = new Date(year, month, 0).getDate()
  const end = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`
  return { start, end }
}

/** 시작~종료 사이의 날짜 키 배열 (기간 일정을 일자별로 전개할 때 사용) */
export function expandDateRange(startDate: string, endDate: string): string[] {
  const result: string[] = []
  const start = new Date(`${startDate}T00:00:00`)
  const end = new Date(`${endDate}T00:00:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return result

  const cursor = new Date(start)
  // 무한 루프 방지 (최대 366일)
  let guard = 0
  while (cursor <= end && guard < 366) {
    result.push(toDateKey(cursor))
    cursor.setDate(cursor.getDate() + 1)
    guard += 1
  }
  return result
}

/** 두 날짜 키 사이 일수 (당일이면 0) */
export function diffDays(startDate: string, endDate: string): number {
  const start = new Date(`${startDate}T00:00:00`).getTime()
  const end = new Date(`${endDate}T00:00:00`).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return 0
  return Math.round((end - start) / (1000 * 60 * 60 * 24))
}

/** 표시용 날짜 (2026. 03. 15. (일)) */
export function formatScheduleDate(dateKey: string): string {
  if (!dateKey) return "-"
  const date = new Date(`${dateKey}T00:00:00`)
  if (Number.isNaN(date.getTime())) return dateKey
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"]
  return `${date.getFullYear()}. ${String(date.getMonth() + 1).padStart(2, "0")}. ${String(
    date.getDate(),
  ).padStart(2, "0")}. (${dayNames[date.getDay()]})`
}

/** 기간 표시 (당일이면 하루만) */
export function formatScheduleRange(startDate: string, endDate: string): string {
  if (startDate === endDate) return formatScheduleDate(startDate)
  return `${formatScheduleDate(startDate)} ~ ${formatScheduleDate(endDate)}`
}

/** 일정 제목(비어 있으면 유형명) */
export function getScheduleTitle(schedule: TeamSchedule): string {
  const trimmed = (schedule.title ?? "").trim()
  if (trimmed) return trimmed
  return SCHEDULE_TYPE_META[schedule.scheduleType]?.label ?? "일정"
}

/** 대상자 표시 (4명 초과 시 "김철수 외 2명") */
export function formatAttendees(attendees: ScheduleAttendee[]): string {
  if (!attendees || attendees.length === 0) return "-"
  const names = attendees.map((a) => a.userName || "이름없음")
  if (names.length <= 4) return names.join(", ")
  return `${names[0]} 외 ${names.length - 1}명`
}

// ==================== 조회/저장 훅 ====================

export function useTeamSchedules() {
  const [schedules, setSchedules] = useState<TeamSchedule[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * 표시 월의 일정 조회.
   * 기간 일정도 포함되어야 하므로 (startDate <= 월말 AND endDate >= 월초) 조건을 사용한다.
   */
  const fetchMonth = useCallback(async (year: number, month: number) => {
    setLoading(true)
    setError(null)
    try {
      const { start, end } = getMonthRange(year, month)
      const { list } = await lumi.entities.team_schedules.list({
        filter: {
          startDate: { $lte: end },
          endDate: { $gte: start },
        },
        sort: { startDate: 1 },
        limit: 500,
      })
      setSchedules((list ?? []) as TeamSchedule[])
      return (list ?? []) as TeamSchedule[]
    } catch (e) {
      console.error("일정 조회 실패:", e)
      setError("일정을 불러오지 못했습니다.")
      setSchedules([])
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  const createSchedule = useCallback(async (input: ScheduleInput) => {
    // creator / createdAt / updatedAt 은 플랫폼이 서버에서 채우므로 보내지 않는다.
    const result = await lumi.entities.team_schedules.create({
      ...input,
    })
    return result
  }, [])

  const updateSchedule = useCallback(
    async (id: string, input: Partial<ScheduleInput>) => {
      // update(id, data) 순서. updatedAt 은 서버가 갱신한다.
      const result = await lumi.entities.team_schedules.update(id, { ...input })
      return result
    },
    [],
  )

  const deleteSchedule = useCallback(async (id: string) => {
    // delete 는 ID 문자열만 받는다.
    await lumi.entities.team_schedules.delete(id)
  }, [])

  return {
    schedules,
    loading,
    error,
    fetchMonth,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    setSchedules,
  }
}

// ==================== 권한 ====================

/** 등록자 본인이거나 Admin이면 수정/삭제 가능 */
export function canEditSchedule(
  schedule: TeamSchedule,
  userId?: string,
  role?: string,
): boolean {
  if (!userId) return false
  if (role === "ADMIN") return true
  return schedule.creator === userId
}
